import { v4 as uuidv4 } from 'uuid';
import bcrypt from 'bcryptjs';
import { supabase } from '@/integrations/supabase/client';
import { normalizeAgeGroup } from '@/utils/ageUtils';

export interface InternalUser {
  id: string;
  username: string;
  password?: string;
  role: string;
  age_group: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  is_active: boolean;
  pin?: string;
  account_verified?: boolean;
  password_requirements_met?: boolean;
}

interface RegistrationData {
  username: string;
  password: string;
  pin: string;
  seedPhrase: string;
  role: string;
  ageGroup: string;
  referralCode?: string;
}

export const internalAuthService = {
  // Initialize by syncing existing users between localStorage and Supabase
  async initializeUsers(): Promise<void> {
    try {
      console.log('InternalAuth: Initializing user data synchronization...');
      
      // Check if we have a current session - if so, don't interfere with it
      const currentSession = localStorage.getItem('internal_user_session');
      if (currentSession) {
        console.log('InternalAuth: Active session found, skipping user sync to prevent conflicts');
        return;
      }
      
      // Get users from localStorage first
      const localUsers = JSON.parse(localStorage.getItem('internal_users') || '[]');
      console.log('InternalAuth: Found local users:', localUsers.length);
      
      // Only sync to Supabase if we have local users, don't wait for Supabase fetch during init
      if (localUsers.length > 0) {
        // Ensure these users exist in Supabase asynchronously
        this.syncLocalUsersToSupabase(localUsers).catch(error => {
          console.error('InternalAuth: Background sync failed:', error);
        });
      }
      
      console.log('InternalAuth: User initialization complete. Local users:', localUsers.length);
    } catch (error) {
      console.error('InternalAuth: Error during user initialization:', error);
    }
  },

  async syncLocalUsersToSupabase(localUsers: any[]): Promise<void> {
    try {
      const { data: supabaseUsers, error } = await supabase
        .from('internal_users')
        .select('username');

      if (error) {
        console.error('InternalAuth: Error fetching Supabase users for sync:', error);
        return;
      }

      const existingUsernames = new Set(supabaseUsers?.map(u => u.username) || []);

      // Sync any local-only users to Supabase
      for (const localUser of localUsers) {
        if (!existingUsernames.has(localUser.username)) {
          console.log('InternalAuth: Syncing local user to Supabase:', localUser.username);
          await this.syncUserToSupabase(localUser);
        }
      }
    } catch (error) {
      console.error('InternalAuth: Error syncing users to Supabase:', error);
    }
  },

  async syncUserToSupabase(localUser: any): Promise<void> {
    try {
      // Check if user already exists in Supabase
      const { data: existingUser } = await supabase
        .from('internal_users')
        .select('id')
        .eq('username', localUser.username)
        .single();

      if (existingUser) {
        console.log('InternalAuth: User already exists in Supabase:', localUser.username);
        return;
      }

      // Hash password and PIN if they're in plain text
      const passwordHash = localUser.password_hash || await bcrypt.hash(localUser.password, 10);
      const pinHash = localUser.pin_hash || await bcrypt.hash(localUser.pin, 10);
      const seedPhraseHash = localUser.seed_phrase_hash || await bcrypt.hash(localUser.seed_phrase, 10);

      // Insert user into Supabase
      const { error } = await supabase
        .from('internal_users')
        .insert({
          id: localUser.id,
          username: localUser.username,
          password_hash: passwordHash,
          pin_hash: pinHash,
          seed_phrase: localUser.seed_phrase,
          seed_phrase_hash: seedPhraseHash,
          role: localUser.role,
          age_group: localUser.age_group,
          created_at: localUser.created_at,
          updated_at: localUser.updated_at,
          last_login: localUser.last_login,
          is_active: localUser.is_active,
          account_verified: localUser.account_verified,
          password_requirements_met: localUser.password_requirements_met
        });

      if (error) {
        console.error('InternalAuth: Error syncing user to Supabase:', error);
      } else {
        console.log('InternalAuth: Successfully synced user to Supabase:', localUser.username);
      }
    } catch (error) {
      console.error('InternalAuth: Error syncing user to Supabase:', error);
    }
  },

  async register(userData: RegistrationData): Promise<{ success: boolean; user?: InternalUser; error?: string }> {
    try {
      const { username, password, pin, seedPhrase, role, ageGroup, referralCode } = userData;
      
      console.log('InternalAuth: Registration attempt for username:', username, 'age group:', ageGroup);

      // Check if username exists in localStorage
      const existingUsers = JSON.parse(localStorage.getItem('internal_users') || '[]');
      if (existingUsers.find((user: any) => user.username === username)) {
        console.log('InternalAuth: Username already exists in localStorage:', username);
        return { success: false, error: 'Username already exists' };
      }

      // Check if username exists in Supabase
      const { data: existingSupabaseUser } = await supabase
        .from('internal_users')
        .select('username')
        .eq('username', username)
        .single();

      if (existingSupabaseUser) {
        console.log('InternalAuth: Username already exists in Supabase:', username);
        return { success: false, error: 'Username already exists' };
      }

      const newUserId = uuidv4();
      const now = new Date().toISOString();

      // Hash credentials
      const passwordHash = await bcrypt.hash(password, 10);
      const pinHash = await bcrypt.hash(pin, 10);
      const seedPhraseHash = await bcrypt.hash(seedPhrase, 10);

      // Normalize age group to ensure consistency
      const normalizedAgeGroup = normalizeAgeGroup(ageGroup as any);
      console.log('InternalAuth: Normalized age group from', ageGroup, 'to', normalizedAgeGroup);

      const newUser: InternalUser = {
        id: newUserId,
        username,
        role,
        age_group: normalizedAgeGroup,
        created_at: now,
        updated_at: now,
        is_active: true,
        account_verified: true,
        password_requirements_met: true
      };

      const newUserData = {
        ...newUser,
        password_hash: passwordHash,
        pin_hash: pinHash,
        seed_phrase: seedPhrase,
        seed_phrase_hash: seedPhraseHash
      };

      // Store in Supabase first (primary storage)
      const { error: supabaseError } = await supabase
        .from('internal_users')
        .insert(newUserData);

      if (supabaseError) {
        console.error('InternalAuth: Error storing user in Supabase:', supabaseError);
        return { success: false, error: 'Failed to register user' };
      }

      // Create user points record with proper referral code
      try {
        console.log('Creating user points record for new internal user:', newUserId);
        
        // Generate a referral code first
        const { data: newCode, error: codeError } = await supabase.rpc('generate_referral_code');
        
        if (codeError) {
          console.error('Error generating referral code:', codeError);
          // Fallback to a temporary code if generation fails
          const fallbackCode = Math.random().toString(36).substring(2, 10).toUpperCase();
          
          const { error: pointsError } = await supabase
            .from('user_points')
            .insert({
              user_id: newUserId,
              user_type: 'internal',
              total_points: 0,
              current_level: 'Bronze',
              referral_code: fallbackCode
            });

          if (pointsError) {
            console.error('Error creating user points record:', pointsError);
          }
        } else {
          // Use the generated code
          const { error: pointsError } = await supabase
            .from('user_points')
            .insert({
              user_id: newUserId,
              user_type: 'internal',
              total_points: 0,
              current_level: 'Bronze',
              referral_code: newCode
            });

          if (pointsError) {
            console.error('Error creating user points record:', pointsError);
          } else {
            console.log('User points record created successfully with code:', newCode);
          }
        }
      } catch (pointsError) {
        console.error('Error setting up user points:', pointsError);
        // Don't fail registration if points setup fails
      }

      // CRITICAL: Insert role into public.user_roles for authoritative role source
      try {
        console.log('InternalAuth: Creating user_roles entry for:', username, 'with role:', role);
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: newUserId,
            user_type: 'internal',
            role: role as "admin" | "agent" | "child" | "coach" | "creative" | "fan" | "parent" | "player" | "scorekeeper" | "support" | "team-owner" | "venue",
            is_active: true,
            assigned_at: now
          });

        if (roleError) {
          console.error('InternalAuth: Error creating user_roles entry:', roleError);
          // Don't fail registration if role insert fails - role is still in internal_users table
        } else {
          console.log('InternalAuth: user_roles entry created successfully');
        }
      } catch (roleError) {
        console.error('InternalAuth: Error setting up user role:', roleError);
        // Don't fail registration if role setup fails
      }

      // Store in localStorage as cache
      existingUsers.push(newUserData);
      localStorage.setItem('internal_users', JSON.stringify(existingUsers));

      // Store session
      localStorage.setItem('internal_user_session', JSON.stringify({
        user: newUser,
        timestamp: Date.now()
      }));

      // Process referral code if provided
      if (referralCode) {
        await this.processReferralCode(referralCode, newUserId);
      }

      console.log('InternalAuth: Registration successful for:', username);
      return { success: true, user: newUser };
    } catch (error) {
      console.error('InternalAuth: Registration error:', error);
      return { success: false, error: 'Failed to register user' };
    }
  },

  async login(username: string, password: string, pin: string): Promise<{ success: boolean; user?: InternalUser; error?: string }> {
    try {
      console.log('InternalAuth: Attempting login for username:', username);
      
      // First try Supabase (authoritative source)
      let userData = null;
      const { data: supabaseUser } = await supabase
        .from('internal_users')
        .select('*')
        .eq('username', username)
        .single();

      if (supabaseUser) {
        userData = supabaseUser;
        console.log('InternalAuth: Found user in Supabase:', username);
      } else {
        // Fallback to localStorage
        const localUsers = JSON.parse(localStorage.getItem('internal_users') || '[]');
        userData = localUsers.find((u: any) => u.username === username);
        
        if (userData) {
          console.log('InternalAuth: Found user in localStorage:', username);
          // Sync to Supabase for future consistency
          await this.syncUserToSupabase(userData);
        }
      }

      if (!userData) {
        console.log('InternalAuth: User not found:', username);
        return { success: false, error: 'Invalid credentials' };
      }

      // Check credentials
      let passwordValid = false;
      let pinValid = false;

      if (userData.password_hash && userData.pin_hash) {
        // Hashed passwords
        console.log('InternalAuth: Validating hashed credentials for:', username);
        passwordValid = await bcrypt.compare(password, userData.password_hash);
        pinValid = await bcrypt.compare(pin, userData.pin_hash);
      } else if (userData.password && userData.pin) {
        // Plain text passwords (legacy support)
        console.log('InternalAuth: Validating plain text credentials for:', username);
        passwordValid = password === userData.password;
        pinValid = pin === userData.pin;
        
        // Upgrade to hashed format if credentials are valid
        if (passwordValid && pinValid) {
          console.log('InternalAuth: Upgrading credentials to hashed format for:', username);
          const passwordHash = await bcrypt.hash(password, 10);
          const pinHash = await bcrypt.hash(pin, 10);
          
          // Update in Supabase
          await supabase
            .from('internal_users')
            .update({
              password_hash: passwordHash,
              pin_hash: pinHash
            })
            .eq('id', userData.id);

          // Update in localStorage
          const localUsers = JSON.parse(localStorage.getItem('internal_users') || '[]');
          const updatedUsers = localUsers.map((u: any) => {
            if (u.username === username) {
              return {
                ...u,
                password_hash: passwordHash,
                pin_hash: pinHash,
                password: undefined,
                pin: undefined
              };
            }
            return u;
          });
          localStorage.setItem('internal_users', JSON.stringify(updatedUsers));
        }
      }

      console.log('InternalAuth: Credential validation results for:', username, 'Password valid:', passwordValid, 'PIN valid:', pinValid);

      if (!passwordValid || !pinValid) {
        console.log('InternalAuth: Invalid credentials for:', username);
        return { success: false, error: 'Invalid credentials' };
      }

      const user: InternalUser = {
        id: userData.id,
        username: userData.username,
        role: userData.role,
        age_group: userData.age_group,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
        is_active: userData.is_active,
        account_verified: userData.account_verified,
        password_requirements_met: userData.password_requirements_met,
        last_login: new Date().toISOString()
      };

      // Update last login
      await this.updateLastLogin(user.id);

      // Backfill user_roles if missing (for users created before this change)
      try {
        const { data: existingRole } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', user.id)
          .eq('user_type', 'internal')
          .eq('is_active', true)
          .single();

        if (!existingRole) {
          console.log('InternalAuth: Backfilling user_roles for existing user:', username);
          await supabase
            .from('user_roles')
            .insert({
              user_id: user.id,
              user_type: 'internal',
              role: user.role as "admin" | "agent" | "child" | "coach" | "creative" | "fan" | "parent" | "player" | "scorekeeper" | "support" | "team-owner" | "venue",
              is_active: true,
              assigned_at: new Date().toISOString()
            });
        }
      } catch (backfillError) {
        console.error('InternalAuth: Error backfilling user_roles:', backfillError);
        // Non-fatal - continue with login
      }

      // Store session
      localStorage.setItem('internal_user_session', JSON.stringify({
        user,
        timestamp: Date.now()
      }));

      console.log('InternalAuth: Login successful for:', username);
      return { success: true, user };
    } catch (error) {
      console.error('InternalAuth: Login error:', error);
      return { success: false, error: 'Failed to log in' };
    }
  },

  logout(): void {
    localStorage.removeItem('internal_user_session');
  },

  getCurrentUser(): InternalUser | null {
    try {
      const session = localStorage.getItem('internal_user_session');
      if (!session) {
        console.log('InternalAuth: No session found');
        return null;
      }
      
      const sessionData = JSON.parse(session);
      const { user, timestamp } = sessionData;
      
      // Validate session timestamp (expire after 24 hours)
      const sessionAge = Date.now() - timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (sessionAge > maxAge) {
        console.log('InternalAuth: Session expired, clearing');
        localStorage.removeItem('internal_user_session');
        return null;
      }
      
      // Additional validation - ensure user object is valid
      if (!user || !user.id || !user.username) {
        console.log('InternalAuth: Invalid user object in session, clearing');
        localStorage.removeItem('internal_user_session');
        return null;
      }
      
      console.log('InternalAuth: Valid session found for user:', user.username);
      return user;
    } catch (error) {
      console.error('InternalAuth: Error getting current user:', error);
      localStorage.removeItem('internal_user_session');
      return null;
    }
  },

  async updateLastLogin(userId: string): Promise<boolean> {
    try {
      const timestamp = new Date().toISOString();
      console.log('InternalAuth: Updating last_login for user:', userId, 'to:', timestamp);
      
      // Update Supabase with proper error handling
      const { data, error } = await supabase
        .from('internal_users')
        .update({ last_login: timestamp })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('InternalAuth: Failed to update last_login in Supabase:', error);
        // Continue to update localStorage even if Supabase fails
      } else {
        console.log('InternalAuth: Successfully updated last_login in Supabase for user:', userId);
      }

      // Update localStorage
      const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
      const updatedUsers = users.map((user: any) => {
        if (user.id === userId) {
          return { ...user, last_login: timestamp };
        }
        return user;
      });
      localStorage.setItem('internal_users', JSON.stringify(updatedUsers));
      
      console.log('InternalAuth: Updated last_login in localStorage for user:', userId);

      return true;
    } catch (error) {
      console.error('InternalAuth: Error updating last login:', error);
      return false;
    }
  },

  async resetPassword(username: string, newPassword: string, confirmPassword: string, seedPhrase: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (newPassword !== confirmPassword) {
        return { success: false, error: 'Passwords do not match' };
      }

      const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
      const userIndex = users.findIndex((user: any) => user.username === username);
      
      if (userIndex === -1) {
        return { success: false, error: 'User not found' };
      }

      const user = users[userIndex];
      const seedPhraseValid = await bcrypt.compare(seedPhrase, user.seed_phrase_hash);
      
      if (!seedPhraseValid) {
        return { success: false, error: 'Invalid seed phrase' };
      }

      const passwordHash = await bcrypt.hash(newPassword, 10);
      users[userIndex] = { ...user, password_hash: passwordHash, updated_at: new Date().toISOString() };
      
      localStorage.setItem('internal_users', JSON.stringify(users));

      // Update in Supabase
      await supabase
        .from('internal_users')
        .update({ password_hash: passwordHash, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      return { success: true };
    } catch (error) {
      console.error('Error resetting password:', error);
      return { success: false, error: 'Failed to reset password' };
    }
  },

  async resetPin(username: string, newPin: string, confirmPin: string, seedPhrase: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (newPin !== confirmPin) {
        return { success: false, error: 'PINs do not match' };
      }

      const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
      const userIndex = users.findIndex((user: any) => user.username === username);
      
      if (userIndex === -1) {
        return { success: false, error: 'User not found' };
      }

      const user = users[userIndex];
      const seedPhraseValid = await bcrypt.compare(seedPhrase, user.seed_phrase_hash);
      
      if (!seedPhraseValid) {
        return { success: false, error: 'Invalid seed phrase' };
      }

      const pinHash = await bcrypt.hash(newPin, 10);
      users[userIndex] = { ...user, pin_hash: pinHash, updated_at: new Date().toISOString() };
      
      localStorage.setItem('internal_users', JSON.stringify(users));

      // Update in Supabase
      await supabase
        .from('internal_users')
        .update({ pin_hash: pinHash, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      return { success: true };
    } catch (error) {
      console.error('Error resetting PIN:', error);
      return { success: false, error: 'Failed to reset PIN' };
    }
  },

  async updatePassword(userId: string, oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
      const userIndex = users.findIndex((user: any) => user.id === userId);
      
      if (userIndex === -1) {
        return { success: false, error: 'User not found' };
      }

      const user = users[userIndex];
      const oldPasswordValid = await bcrypt.compare(oldPassword, user.password_hash);
      if (!oldPasswordValid) {
        return { success: false, error: 'Current password is incorrect' };
      }

      const passwordHash = await bcrypt.hash(newPassword, 10);
      users[userIndex] = { ...user, password_hash: passwordHash, updated_at: new Date().toISOString() };
      
      localStorage.setItem('internal_users', JSON.stringify(users));

      // Update in Supabase
      await supabase
        .from('internal_users')
        .update({ password_hash: passwordHash, updated_at: new Date().toISOString() })
        .eq('id', userId);

      return { success: true };
    } catch (error) {
      console.error('Error updating password:', error);
      return { success: false, error: 'Failed to update password' };
    }
  },

  async updatePin(userId: string, oldPin: string, newPin: string): Promise<{ success: boolean; error?: string }> {
    try {
      const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
      const userIndex = users.findIndex((user: any) => user.id === userId);
      
      if (userIndex === -1) {
        return { success: false, error: 'User not found' };
      }

      const user = users[userIndex];
      const oldPinValid = await bcrypt.compare(oldPin, user.pin_hash);
      if (!oldPinValid) {
        return { success: false, error: 'Current PIN is incorrect' };
      }

      const pinHash = await bcrypt.hash(newPin, 10);
      users[userIndex] = { ...user, pin_hash: pinHash, updated_at: new Date().toISOString() };
      
      localStorage.setItem('internal_users', JSON.stringify(users));

      // Update in Supabase
      await supabase
        .from('internal_users')
        .update({ pin_hash: pinHash, updated_at: new Date().toISOString() })
        .eq('id', userId);

      return { success: true };
    } catch (error) {
      console.error('Error updating PIN:', error);
      return { success: false, error: 'Failed to update PIN' };
    }
  },

  async getUserById(userId: string): Promise<InternalUser | null> {
    try {
      // Try Supabase first
      const { data: supabaseUser } = await supabase
        .from('internal_users')
        .select('*')
        .eq('id', userId)
        .single();

      let userData = null;
      if (supabaseUser) {
        userData = supabaseUser;
      } else {
        // Fallback to localStorage
        const users = JSON.parse(localStorage.getItem('internal_users') || '[]');
        userData = users.find((u: any) => u.id === userId);
      }
      
      if (!userData) return null;

      return {
        id: userData.id,
        username: userData.username,
        role: userData.role,
        age_group: userData.age_group,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
        last_login: userData.last_login,
        is_active: userData.is_active,
        account_verified: userData.account_verified,
        password_requirements_met: userData.password_requirements_met
      };
    } catch (error) {
      console.error('Error getting user by ID:', error);
      return null;
    }
  },

  async getAllUsers(): Promise<InternalUser[]> {
    try {
      // Get from Supabase (authoritative source)
      const { data: supabaseUsers } = await supabase
        .from('internal_users')
        .select('*')
        .eq('role', 'player');

      const allUsers = supabaseUsers || [];
      
      // Also get from localStorage and add any that aren't in Supabase
      const localUsers = JSON.parse(localStorage.getItem('internal_users') || '[]');
      localUsers.forEach((localUser: any) => {
        if (!allUsers.find(u => u.id === localUser.id)) {
          allUsers.push(localUser);
        }
      });

      console.log('Getting all internal users:', allUsers.length);
      
      return allUsers.map((userData: any) => ({
        id: userData.id,
        username: userData.username,
        role: userData.role,
        age_group: userData.age_group,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
        last_login: userData.last_login,
        is_active: userData.is_active,
        account_verified: userData.account_verified,
        password_requirements_met: userData.password_requirements_met
      }));
    } catch (error) {
      console.error('Error getting all users:', error);
      return [];
    }
  },

  async searchUsers(query: string): Promise<InternalUser[]> {
    try {
      const allUsers = await this.getAllUsers();
      const filteredUsers = allUsers.filter((user) =>
        user.username.toLowerCase().includes(query.toLowerCase()) && user.role === 'player'
      );
      
      console.log(`Searching internal users for "${query}":`, filteredUsers.length, 'results');
      return filteredUsers;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  },

  async processReferralCode(referralCode: string, newUserId: string): Promise<boolean> {
    try {
      // Use the database function to process referral codes consistently
      const { data, error } = await supabase.rpc('process_referral', {
        p_referral_code: referralCode.toUpperCase(),
        p_new_user_id: newUserId,
        p_new_user_type: 'internal'
      });

      if (error) {
        console.error('Error processing referral code:', error);
        return false;
      }

      console.log('Referral processed successfully:', data);
      return data === true;
    } catch (error) {
      console.error('Error processing referral code:', error);
      return false;
    }
  },

  generateReferralCodeForUser(userId: string): string {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const code = Math.abs(hash).toString(36).toUpperCase().substring(0, 8);
    return code.padEnd(8, '0');
  },

  async validateReferralCode(referralCode: string): Promise<{ valid: boolean; username?: string }> {
    try {
      // Check in user_points table for both internal and supabase users
      const { data: pointsData } = await supabase
        .from('user_points')
        .select('user_id, user_type')
        .eq('referral_code', referralCode.toUpperCase())
        .single();

      if (pointsData) {
        // Get username based on user type
        if (pointsData.user_type === 'internal') {
          const { data: internalUser } = await supabase
            .from('internal_users')
            .select('username')
            .eq('id', pointsData.user_id)
            .single();
          
          return { valid: true, username: internalUser?.username };
        } else {
          const { data: supabaseUser } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', pointsData.user_id)
            .single();
          
          return { valid: true, username: supabaseUser?.username };
        }
      }

      return { valid: false };
    } catch (error) {
      console.error('Error validating referral code:', error);
      return { valid: false };
    }
  }
};
